/**
 * EINE Zustandsableitung für alle Portale (RE-01 ff.). Eingabe: Rechnungsdatensatz +
 * Kontext mit „heute“. Keine Uhrzeit, kein DOM, keine Datenbank.
 *
 * Zusätzlich: erlaubte Aktionen je Handelndem (allowedActions/checkAction), die
 * Übergänge selbst (applyAction), Primäraktion, Dringlichkeitsrang, Eimer, Filter.
 */
import {
  checkCollectionHandover,
  checkDunning,
  daysSinceOriginalDue,
  dunningDeadlineEnd,
  dunningDeadlineFor,
  getStage,
  isCollectionDue,
  isFinalStage,
  finalStage,
  type DunningContext,
} from "./dunning.js";
import {
  awaitsDelivery,
  confirmationActive,
  currentDueDate,
  isBadDebt,
  overdueBase,
  hasOpenPaymentCheck,
  inCollection,
  inPaymentRun,
  isApproved,
  isCancelled,
  isDiscarded,
  isLive,
  isPaid,
  isSent,
  isWithdrawn,
  needsCorrection,
  originalDueDate,
  partialPayoutOpen,
  talentSaysStillOverdue,
} from "./invoice-facts.js";
import { normalizeInvoiceNumber } from "./invoice-number.js";
import { addDays, diffDays, dueDateFrom, isIsoDate } from "./periods.js";
import {
  DUNNING_COLLECTION,
  DUNNING_NONE,
  DomainError,
  type ActorPermissions,
  type Cents,
  type Currency,
  type Finding,
  type InvoiceRecord,
  type IsoDate,
  type OrgSettings,
} from "./types.js";

// ---------------------------------------------------------------------------
// Typen
// ---------------------------------------------------------------------------

export type InvoiceState =
  | "discarded"
  | "withdrawn"
  | "cancelled"
  | "cancelled_refund_pending"
  | "credit_note"
  | "paid"
  /** Forderung als uneinbringlich ausgebucht (0055) */
  | "bad_debt"
  | "collection"
  | "correction_needed"
  | "money_missing"
  | "payment_reported"
  | "dunning"
  | "send_overdue"
  /** Versendet an die Marke, Rechnungsmail noch unterwegs bzw. gescheitert: keine Frist, kein Verzug (0055) */
  | "delivery_pending"
  | "delivery_failed"
  /** Creator-Rechnung an die Agentur: Auszahlung überfällig bzw. ausgezahlt, Bestätigung des Creators offen (0055) */
  | "payout_overdue"
  | "payout_confirmed"
  | "overdue"
  | "due_today"
  | "due_soon"
  | "sent"
  | "approved"
  | "in_review"
  | "draft";

export type Responsible = "agency" | "talent" | "brand" | "none";

export type StateSettings = Pick<
  OrgSettings,
  "dueSoonDays" | "paymentCheckCooldownDays" | "stillOverdueRecheckDays" | "talentAnswerDays" | "defaultPaymentTermDays"
>;

export interface InvoiceStateContext extends DunningContext {
  settings: StateSettings;
  /** RE-26: passt der Leistungsmonat zu einer Monatszeile des Deals? (false = nein, undefined = keine Monatszeilen) */
  revenueMonthMatchesLine?: boolean;
}

export interface InvoiceStateResult {
  state: InvoiceState;
  labelKey: string;
  labelParams: Record<string, string | number>;
  /** Mahnstufe bei state 'dunning' */
  stageKey: string | null;
  isOverdue: boolean;
  daysOverdue: number;
  canDun: boolean;
  /** Grund, warum nicht gemahnt werden kann (null, wenn canDun) */
  blockedReason: string | null;
  responsible: Responsible;
  /** MA-13: letzte Stufe, Frist abgelaufen, keine Sperre → Inkasso fällig (Anzeige) */
  collectionDue: boolean;
  dunningDeadlineEnd: IsoDate | null;
  confirmationActive: boolean;
  openPaymentCheck: boolean;
}

const DONE_STATES: ReadonlySet<InvoiceState> = new Set(["discarded", "withdrawn", "cancelled", "paid", "bad_debt", "credit_note"]);

/** Rechnung der Agentur (an die Marke oder an den Creator): die Agentur ist Gläubigerin, es gibt keine Nachfrage beim Creator. */
export const isAgencyIssued = (inv: InvoiceRecord): boolean => inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent";

// ---------------------------------------------------------------------------
// Zustandsableitung (RE-01 … RE-12)
// ---------------------------------------------------------------------------

/** Rechnung des Creators an die Agentur, deren Auszahlung die Agentur vermerkt hat (der Creator hat noch nicht bestätigt) */
export function paidOutUnconfirmed(inv: Pick<InvoiceRecord, "recipientIsOwnOrg" | "adminPaymentConfirmedAt">): boolean {
  return !!inv.recipientIsOwnOrg && inv.adminPaymentConfirmedAt != null;
}

export function deriveInvoiceState(inv: InvoiceRecord, ctx: InvoiceStateContext): InvoiceStateResult {
  const today = ctx.today;
  const due = currentDueDate(inv);
  const dunCheck = checkDunning(inv, ctx);
  const base = {
    stageKey: null as string | null,
    isOverdue: false,
    daysOverdue: 0,
    canDun: dunCheck.allowed,
    blockedReason: dunCheck.allowed ? null : dunCheck.blockedReason,
    collectionDue: false,
    dunningDeadlineEnd: null as IsoDate | null,
    confirmationActive: confirmationActive(inv),
    openPaymentCheck: hasOpenPaymentCheck(inv),
  };
  const make = (state: InvoiceState, responsible: Responsible, labelParams: Record<string, string | number> = {}, extra: Partial<InvoiceStateResult> = {}): InvoiceStateResult => ({
    ...base,
    state,
    labelKey: `invoice.state.${state}`,
    labelParams,
    responsible,
    ...extra,
  });

  // Rangfolge: erster Treffer gewinnt
  if (isDiscarded(inv)) return make("discarded", "none");
  if (isWithdrawn(inv)) return make("withdrawn", "none");
  if (isCancelled(inv)) {
    // Auch eine Teilzahlung muss erstattet bzw. verrechnet werden (Befund 10)
    // Praxistest K-157: Hat die Agentur die Auszahlung vermerkt, ist das Geld geflossen, auch wenn der Creator den Eingang
    // noch nicht bestätigt hat (dann fehlen paidAt und paidCents): Rückforderung offen
    return isPaid(inv) || (inv.paidCents ?? 0) > 0 || paidOutUnconfirmed(inv) ? make("cancelled_refund_pending", "agency") : make("cancelled", "none");
  }
  // Stornorechnung (0043): nach dem Versand abgeschlossen, kein Zahlungslauf und keine Mahnung
  if (inv.creditFor && isApproved(inv) && isSent(inv)) return make("credit_note", "none");
  if (isPaid(inv)) return isBadDebt(inv) ? make("bad_debt", "none") : make("paid", "none");
  if (inCollection(inv)) return make("collection", "brand", inv.collectionAt ? { date: inv.collectionAt } : {});
  if (needsCorrection(inv)) return make("correction_needed", "talent");
  if (confirmationActive(inv) && inv.talentPaymentResponse === "still_overdue" && !hasOpenPaymentCheck(inv)) {
    const p: Record<string, string | number> = {};
    if (due) p.dueDate = due;
    return make("money_missing", "agency", p, overdueExtra(inv, today));
  }
  if (inv.talentPaymentResponse === "paid") return make("payment_reported", "agency", inv.talentPaymentResponseAt ? { date: inv.talentPaymentResponseAt } : {});

  if (isApproved(inv)) {
    if (!isSent(inv)) {
      // Rechnung des Creators an die Agentur: nicht „Versand“, sondern Auszahlung, fällig nach dem Auszahlungsmodus (Befund 7/8)
      if (inv.recipientIsOwnOrg) {
        const pdue = overdueBase(inv);
        if (pdue && pdue < today) {
          const days = diffDays(pdue, today);
          return make("payout_overdue", "agency", { dueDate: pdue, days }, { isOverdue: true, daysOverdue: days });
        }
        return make("approved", "agency", pdue ? { dueDate: pdue } : {});
      }
      if (due && due < today) return make("send_overdue", "agency", { dueDate: due });
      return make("approved", "agency");
    }
    // Rechnung an die Marke noch nicht zugegangen: keine Frist, kein Verzug, keine Mahnung (Kommunikation 2)
    if (awaitsDelivery(inv)) {
      return inv.deliveryFailedAt ? make("delivery_failed", "agency", { date: inv.deliveryFailedAt }) : make("delivery_pending", "agency");
    }
    // Die Agentur hat ausgezahlt; der Creator bestätigt den Eingang (Befund 9): kein Verzug der Agentur
    if (inv.recipientIsOwnOrg && confirmationActive(inv)) {
      return make("payout_confirmed", "talent", inv.adminPaymentConfirmedAt ? { date: inv.adminPaymentConfirmedAt } : {});
    }
    const responsible = paymentRunResponsible(inv, ctx);
    const extra = { ...overdueExtra(inv, today), collectionDue: isCollectionDue(inv, ctx), dunningDeadlineEnd: dunningDeadlineEnd(inv, ctx.policy, ctx.holidays) };
    if (getStage(ctx.policy, inv.dunningStage)) {
      const days = daysSinceOriginalDue(inv, today);
      return make("dunning", responsible, { stage: inv.dunningStage, days }, { ...extra, stageKey: inv.dunningStage, daysOverdue: days });
    }
    const base = overdueBase(inv);
    if (!base) return make("sent", responsible, {}, extra);
    const d = diffDays(today, base);
    if (d < 0) return make(inv.recipientIsOwnOrg ? "payout_overdue" : "overdue", responsible, { days: -d }, extra);
    if (d === 0) return make("due_today", responsible, {}, extra);
    if (d <= ctx.settings.dueSoonDays) return make("due_soon", responsible, { days: d }, extra);
    return make("sent", responsible, {}, extra);
  }
  if (inv.reviewStatus === "submitted") return make("in_review", "agency");
  // Probelauf N1: Entwürfe, die die Agentur selbst ausstellt (an Marke oder Creator, Gutschriften), liegen bei der Agentur
  if (inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent" || inv.selfBilled) return make("draft", "agency");
  return make("draft", "talent");
}

function overdueExtra(inv: InvoiceRecord, today: IsoDate): Partial<InvoiceStateResult> {
  const due = overdueBase(inv);
  if (!inPaymentRun(inv) || !due || due >= today) return { isOverdue: false, daysOverdue: 0 };
  return { isOverdue: true, daysOverdue: diffDays(due, today) };
}

/** RE-11/RE-12/MA-14: wer ist im Zahlungslauf am Zug? */
function paymentRunResponsible(inv: InvoiceRecord, ctx: InvoiceStateContext): Responsible {
  if (hasOpenPaymentCheck(inv)) return "talent";
  if (confirmationActive(inv)) return "talent";
  if (inv.recipientIsOwnOrg) return "agency";
  // Rechnung der Agentur an den Creator: zahlen muss der Creator (Befund 14)
  if (inv.kind === "agency_to_talent") return "talent";
  return "brand";
}

// ---------------------------------------------------------------------------
// Aktionen
// ---------------------------------------------------------------------------

export type InvoiceAction =
  // Talent
  | "edit"
  | "submit"
  | "discard"
  | "withdraw_submission"
  | "correct"
  | "report_paid"
  | "report_still_overdue"
  | "open_pdf"
  // Agentur
  | "review"
  | "approve"
  | "reject"
  | "withdraw_approval"
  | "return_to_talent"
  | "send_to_brand"
  | "resend_to_brand"
  | "extend_due_date"
  | "request_payment_check"
  | "confirm_payment"
  | "confirm_payout_and_ask"
  | "revoke_payment_confirmation"
  | "reset_payment"
  | "send_dunning"
  | "set_dunning_stage"
  | "set_dunning_block"
  | "lift_dunning_block"
  | "hand_over_to_collection"
  | "withdraw_collection"
  | "cancel"
  | "check_payment"
  | "check_refund";

const TALENT_ACTIONS: readonly InvoiceAction[] = ["edit", "submit", "discard", "withdraw_submission", "correct", "report_paid", "report_still_overdue", "open_pdf"];
const AGENCY_ACTIONS: readonly InvoiceAction[] = [
  "open_pdf", "review", "approve", "reject", "withdraw_approval", "return_to_talent", "discard", "send_to_brand",
  "resend_to_brand", "extend_due_date", "request_payment_check", "confirm_payment", "confirm_payout_and_ask",
  "revoke_payment_confirmation", "reset_payment", "send_dunning", "set_dunning_stage", "set_dunning_block",
  "lift_dunning_block", "hand_over_to_collection", "withdraw_collection", "cancel", "check_payment", "check_refund",
];

export type PaymentCheckMode = "normal" | "manual" | "preconfirmed" | "again";

export interface ActionPayload {
  reason?: string;
  paymentTermDays?: number;
  newDueDate?: IsoDate;
  paymentProofRef?: string;
  stage?: string;
  deadlineOverride?: IsoDate;
  /** Stufe vor der Inkasso-Übergabe laut Protokoll (MA-11) */
  previousStage?: string;
  mode?: PaymentCheckMode;
}

export interface ActionCheck {
  allowed: boolean;
  reason: string | null;
  /** nicht blockierende Rückfragen */
  confirmations: Finding[];
}

const ok = (confirmations: Finding[] = []): ActionCheck => ({ allowed: true, reason: null, confirmations });
const no = (reason: string): ActionCheck => ({ allowed: false, reason, confirmations: [] });

const EDITABLE = new Set(["not_submitted", "rejected", "revision"]);

/**
 * RE-30: Nachfrage beim Talent („Ist das Geld da?“) – Sperrgrund oder null.
 */
export function paymentCheckBlock(inv: InvoiceRecord, ctx: InvoiceStateContext, mode: PaymentCheckMode = "normal"): Finding | null {
  const f = (code: string, params?: Record<string, string | number>): Finding => ({ code, severity: "error", ...(params ? { params } : {}) });
  if (isPaid(inv)) return f("PAID");
  if (isAgencyIssued(inv)) return f("AGENCY_INVOICE");   // Gläubigerin ist die Agentur selbst
  if (!isApproved(inv) || !isSent(inv)) return f("NOT_APPROVED_OR_NOT_SENT");
  if (inCollection(inv)) return f("IN_COLLECTION");
  if (!inPaymentRun(inv)) return f("NOT_IN_PAYMENT_RUN");  // storniert oder verworfen (R-11)
  if (partialPayoutOpen(inv)) return f("PARTIAL_PAYOUT_OPEN");  // GELD-R3-1: erst der Rest, dann die Nachfrage
  const due = currentDueDate(inv);
  const overdue = !!due && due < ctx.today;
  if (!overdue && mode !== "manual" && mode !== "preconfirmed") return f("NOT_OVERDUE");
  const answered = !!inv.talentPaymentResponse && !hasOpenPaymentCheck(inv);
  const againAllowed = mode === "preconfirmed" || (mode === "again" && inv.talentPaymentResponse === "still_overdue");
  if (answered && !againAllowed) return f("ALREADY_ANSWERED");
  if (inv.overdueCheckRequestedAt && !againAllowed) {
    const until = addDays(inv.overdueCheckRequestedAt, ctx.settings.paymentCheckCooldownDays);
    if (ctx.today < until) return f("COOLDOWN", { since: inv.overdueCheckRequestedAt, until });
  }
  return null;
}

/**
 * Modus einer Nachfrage, wenn die App keinen mitgibt. Derselbe Modus steht im Hauptknopf,
 * damit Knopf und Prüfung nie auseinanderlaufen (R-07): nach eigener Auszahlungsbestätigung
 * „preconfirmed“, bei offener Nachfrage „manual“ (auch vor der Fälligkeit), nach „noch offen“ „again“.
 */
export function paymentCheckModeFor(inv: InvoiceRecord): PaymentCheckMode {
  if (confirmationActive(inv)) return "preconfirmed";
  if (hasOpenPaymentCheck(inv)) return "manual";
  if (talentSaysStillOverdue(inv)) return "again";
  return "normal";
}

/** Prüft EINE Aktion für einen Handelnden. */
export function checkAction(
  inv: InvoiceRecord,
  action: InvoiceAction,
  actor: ActorPermissions,
  ctx: InvoiceStateContext,
  payload: ActionPayload = {},
): ActionCheck {
  if (actor.role === "talent") {
    if (!TALENT_ACTIONS.includes(action)) return no("NOT_ALLOWED_FOR_TALENT");
    if (!actor.isOwnInvoice) return no("NOT_OWN_INVOICE");
  } else if (!AGENCY_ACTIONS.includes(action)) {
    return no("NOT_ALLOWED_FOR_AGENCY");
  }
  const need = (flag: boolean | undefined, perm: string): ActionCheck | null => (flag ? null : no(`MISSING_PERMISSION:${perm}`));
  const live = isLive(inv);
  // Stornorechnung: nur ansehen und versenden (0043)
  if (inv.creditFor && !["open_pdf", "send_to_brand", "resend_to_brand"].includes(action)) return no("CREDIT_NOTE");

  switch (action) {
    case "edit":
    case "correct":
      if (!live) return no("NOT_LIVE");
      if (action === "correct" && !needsCorrection(inv)) return no("NO_CORRECTION_REQUESTED");
      return EDITABLE.has(inv.reviewStatus) ? ok() : no("NOT_EDITABLE");
    case "submit":
      if (!live || !EDITABLE.has(inv.reviewStatus)) return no("NOT_EDITABLE");
      return inv.hasPdf ? ok() : no("PDF_MISSING");
    case "discard":
      if (actor.role === "agency") {
        const p = need(actor.canApproveInvoices, "canApproveInvoices");
        if (p) return p;
      }
      if (!live) return no("NOT_LIVE");
      // RE-17 (Talent) und RE-22 (Agentur): nur Entwürfe und zurückgegebene Rechnungen
      return EDITABLE.has(inv.reviewStatus) ? ok() : no("NOT_DISCARDABLE");
    case "withdraw_submission":
      if (inv.reviewStatus !== "submitted") return no("NOT_SUBMITTED");
      return inv.adminReviewedAt ? no("ALREADY_TOUCHED_BY_AGENCY") : ok();
    case "report_paid":
    case "report_still_overdue": {
      if (!live || !isApproved(inv) || !isSent(inv)) return no("NOT_IN_PAYMENT_RUN");
      if (isPaid(inv) || isCancelled(inv)) return no("CLOSED");
      if (inCollection(inv)) return no("IN_COLLECTION");
      if (inv.talentPaymentResponse === "paid") return no("ALREADY_REPORTED_PAID");
      if (action === "report_still_overdue" && talentSaysStillOverdue(inv)) return no("ALREADY_REPORTED_STILL_OVERDUE");
      // GELD-R3-1: nur ein Teil ausgezahlt, „Geld ist da“ gilt erst nach dem Rest
      if (action === "report_paid" && partialPayoutOpen(inv)) return no("PARTIAL_PAYOUT_OPEN");
      return ok();
    }
    case "open_pdf":
      return inv.hasPdf ? ok() : no("PDF_MISSING");
    case "review":
      return inv.reviewStatus === "submitted" && live ? ok() : no("NOT_IN_REVIEW");
    case "approve":
    case "reject": {
      const p = need(actor.canApproveInvoices, "canApproveInvoices");
      if (p) return p;
      if (!live || inv.reviewStatus !== "submitted") return no("NOT_IN_REVIEW");
      return ok();
    }
    case "withdraw_approval":
    case "return_to_talent": {
      const p = need(actor.canApproveInvoices, "canApproveInvoices");
      if (p) return p;
      // 0043: Rechnungen der Agentur (und ihre Gutschriften, 0046) sind festgeschrieben und werden storniert, nie zurückgenommen
      if (isAgencyIssued(inv) || inv.selfBilled) return no("USE_CANCEL");
      if (!live || !isApproved(inv)) return no("NOT_APPROVED");
      if (isPaid(inv) || isCancelled(inv) || inCollection(inv)) return no("CLOSED");
      if (action === "return_to_talent" && !isSent(inv)) return no("NOT_SENT");
      if (action === "withdraw_approval" && isSent(inv)) return no("ALREADY_SENT");
      return ok();
    }
    case "send_to_brand":
    case "resend_to_brand": {
      // An die eigene Agentur heißt „senden“: in den Zahlungslauf geben (Recht Auszahlungen, wie in der Datenbank)
      const p = inv.recipientIsOwnOrg ? need(actor.canTriggerPayout, "canTriggerPayout") : need(actor.canSendToBrand, "canSendToBrand");
      if (p) return p;
      if (!live || !isApproved(inv) || isPaid(inv) || isCancelled(inv)) return no("NOT_SENDABLE");
      // Auszahlung erst nach Zahlung der Marke (Modus der Agentur): ohne Fälligkeit noch nicht in den Zahlungslauf (Befund 8)
      if (action === "send_to_brand" && inv.recipientIsOwnOrg && inv.payoutDueDate === null) return no("BRAND_NOT_PAID");
      if (action === "send_to_brand") return isSent(inv) ? no("ALREADY_SENT") : ok();
      if (!isSent(inv)) return no("NOT_SENT");
      // RE-24: ausdrückliche Bestätigung mit Anzahl und letztem Datum
      return ok([{ code: "RESEND_CONFIRM", severity: "question", params: { count: inv.sentToBrandCount, lastSentAt: inv.lastSentToBrandAt ?? inv.sentToBrandAt ?? "" } }]);
    }
    case "extend_due_date": {
      // Wie die Datenbank (invoice_extend_due): Mahnen oder Zahlungen bestätigen (Befund 32)
      if (!actor.canDun && !actor.canConfirmPayment) return no("MISSING_PERMISSION:canDun");
      if (!inPaymentRun(inv)) return no("NOT_IN_PAYMENT_RUN");
      const cur = currentDueDate(inv);
      if (payload.newDueDate !== undefined && !isIsoDate(payload.newDueDate)) return no("INVALID_DATE");  // R-12
      if (payload.newDueDate && cur && payload.newDueDate <= cur) return no("EXTENSION_MUST_BE_LATER");
      return ok();
    }
    case "request_payment_check": {
      const mode = payload.mode ?? paymentCheckModeFor(inv);
      const b = paymentCheckBlock(inv, ctx, mode);
      return b ? no(b.code) : ok();
    }
    case "confirm_payment": {
      const p = need(actor.canConfirmPayment, "canConfirmPayment");
      if (p) return p;
      // Rechnung der Agentur an den Creator: Eingang mit Betrag erfassen, nicht ohne Betrag bestätigen (CREATOR-R3-6, 0091)
      if (inv.kind === "agency_to_talent") return no("USE_RECORD_PAYMENT");
      if (!live || !isApproved(inv) || isPaid(inv)) return no("NOT_CONFIRMABLE");
      if (inv.talentPaymentResponse !== "paid") return no("TALENT_HAS_NOT_REPORTED_PAID");
      if (partialPayoutOpen(inv)) return no("PARTIAL_PAYOUT_OPEN");
      if (ctx.revenueMonthMatchesLine === false) return no("REVENUE_MONTH_HAS_NO_LINE");
      return ok();
    }
    case "confirm_payout_and_ask": {
      const p = need(actor.canTriggerPayout, "canTriggerPayout");
      if (p) return p;
      if (!live || !isApproved(inv) || !isSent(inv)) return no("NOT_APPROVED_OR_NOT_SENT");
      if (isPaid(inv)) return no("PAID");
      if (inCollection(inv)) return no("IN_COLLECTION");
      if (!inPaymentRun(inv)) return no("NOT_IN_PAYMENT_RUN");  // R-11
      if (inv.adminPaymentConfirmedAt) return no("ALREADY_CONFIRMED");
      if (inv.talentPaymentResponse === "paid") return no("USE_CONFIRM_PAYMENT");
      if (partialPayoutOpen(inv)) return no("PARTIAL_PAYOUT_OPEN");
      return ok();
    }
    case "revoke_payment_confirmation": {
      if (!actor.canTriggerPayout && !actor.canConfirmPayment) return no("MISSING_PERMISSION:canTriggerPayout");
      if (!inv.adminPaymentConfirmedAt) return no("NOT_CONFIRMED");
      return isPaid(inv) ? no("PAID") : ok();
    }
    case "reset_payment": {
      const p = need(actor.canConfirmPayment, "canConfirmPayment");
      if (p) return p;
      // Praxistest K-127: auch Teilzahlungen lassen sich zurücksetzen (falsch erfasster Betrag oder Tag), wie in der Datenbank
      return isPaid(inv) || (inv.paidCents ?? 0) > 0 || (isCancelled(inv) && paidOutUnconfirmed(inv)) ? ok() : no("NOT_PAID");
    }
    case "send_dunning": {
      const p = need(actor.canDun, "canDun");
      if (p) return p;
      const c = checkDunning(inv, ctx);
      return c.allowed ? ok(c.confirmations) : no(c.blockedReason ?? "NOT_ALLOWED");
    }
    case "set_dunning_stage": {
      const p = need(actor.canDun, "canDun");
      if (p) return p;
      if (!live || !isApproved(inv) || isPaid(inv) || isCancelled(inv)) return no("NOT_IN_DUNNING_PERIOD");
      if (inCollection(inv)) return no("IN_COLLECTION");
      if (inv.recipientIsOwnOrg && payload.stage && payload.stage !== DUNNING_NONE) return no("RECIPIENT_IS_OWN_ORG");
      if (payload.stage !== undefined && payload.stage !== DUNNING_NONE && !getStage(ctx.policy, payload.stage)) return no("UNKNOWN_STAGE");
      return ok();
    }
    case "set_dunning_block":
    case "lift_dunning_block": {
      const p = need(actor.canDun, "canDun");
      if (p) return p;
      if (action === "lift_dunning_block") return inv.dunningBlocked ? ok() : no("NOT_BLOCKED");
      if (inv.dunningBlocked) return no("ALREADY_BLOCKED");
      if (inv.recipientIsOwnOrg) return no("RECIPIENT_IS_OWN_ORG");
      return live && isApproved(inv) && !isPaid(inv) && !isCancelled(inv) ? ok() : no("NOT_IN_DUNNING_PERIOD");
    }
    case "hand_over_to_collection": {
      const p = need(actor.canDun, "canDun");
      if (p) return p;
      const c = checkCollectionHandover(inv, ctx);
      return c.allowed ? ok(c.confirmations) : no(c.blockedReason ?? "NOT_ALLOWED");
    }
    case "withdraw_collection": {
      const p = need(actor.canDun, "canDun");
      if (p) return p;
      return inCollection(inv) ? ok() : no("NOT_IN_COLLECTION");
    }
    case "cancel": {
      const p = need(actor.canApproveInvoices, "canApproveInvoices");
      if (p) return p;
      return live && isApproved(inv) && !isCancelled(inv) ? ok() : no("NOT_CANCELLABLE");
    }
    case "check_payment":
      return deriveInvoiceState(inv, ctx).state === "money_missing" ? ok() : no("NOTHING_TO_CHECK");
    case "check_refund":
      return isCancelled(inv) && isPaid(inv) ? ok() : no("NOTHING_TO_CHECK");
  }
}

/** Alle erlaubten Aktionen eines Handelnden, mit Rückfragen. */
export function allowedActions(
  inv: InvoiceRecord,
  actor: ActorPermissions,
  ctx: InvoiceStateContext,
): { action: InvoiceAction; confirmations: Finding[] }[] {
  const list = actor.role === "talent" ? TALENT_ACTIONS : AGENCY_ACTIONS;
  const out: { action: InvoiceAction; confirmations: Finding[] }[] = [];
  for (const a of list) {
    const c = checkAction(inv, a, actor, ctx);
    if (c.allowed) out.push({ action: a, confirmations: c.confirmations });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Übergänge
// ---------------------------------------------------------------------------

export type ActionResult = { ok: true; invoice: InvoiceRecord } | { ok: false; reason: string };

/** RE-23: Zahlungsfrist = Deal → Marke → Frist auf dem Beleg → Agentur-Standard (nur 1–365 gültig). */
export function resolvePaymentTermDays(input: {
  deal?: number | null;
  brand?: number | null;
  document?: number | null;
  orgDefault: number;
}): number {
  for (const v of [input.deal, input.brand, input.document, input.orgDefault]) {
    if (typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= 365) return v;
  }
  throw new DomainError("INVALID_DEFAULT_PAYMENT_TERM", String(input.orgDefault));
}

const clearDunning = (i: InvoiceRecord): InvoiceRecord => ({ ...i, dunningStage: DUNNING_NONE, lastDunningAt: null, dunningDeadline: null });

/**
 * Wendet eine Aktion an und gibt den neuen Datensatz zurück. Prüft vorher checkAction;
 * Rückfragen (confirmations) muss der Aufrufer vorher eingeholt haben.
 */
export function applyAction(
  inv: InvoiceRecord,
  action: InvoiceAction,
  actor: ActorPermissions,
  ctx: InvoiceStateContext,
  payload: ActionPayload = {},
): ActionResult {
  const c = checkAction(inv, action, actor, ctx, payload);
  if (!c.allowed) return { ok: false, reason: c.reason ?? "NOT_ALLOWED" };
  const t = ctx.today;
  const done = (invoice: InvoiceRecord): ActionResult => ({ ok: true, invoice });

  switch (action) {
    case "submit":
      return done({ ...inv, reviewStatus: "submitted", submittedAt: t });
    case "withdraw_submission":
      // RE-18: zurück auf Entwurf, der Ablehngrund bleibt stehen
      return done({ ...inv, reviewStatus: "not_submitted", submittedAt: null });
    case "discard":
      // RE-17: weich verwerfen, nie löschen
      return done({ ...inv, discardedAt: t });
    case "approve":
      return done({ ...inv, reviewStatus: "approved", adminReviewedAt: t, rejectionReason: null });
    case "reject": {
      if (!payload.reason?.trim()) return { ok: false, reason: "REASON_REQUIRED" };
      // RE-20/MA-18/MA-21-T: Zahl-/Meldestatus und Mahnstufe zurück, Mail-Stempel der Vorrunde weg
      return done(
        clearDunning({
          ...inv,
          reviewStatus: "rejected",
          adminReviewedAt: t,
          rejectionReason: payload.reason.trim(),
          rejectionMailSentAt: null,
          talentPaymentResponse: null,
          talentPaymentResponseAt: null,
          overdueCheckRequestedAt: null,
          adminPaymentConfirmedAt: null,
        }),
      );
    }
    case "withdraw_approval":
    case "return_to_talent": {
      if (!payload.reason?.trim()) return { ok: false, reason: "REASON_REQUIRED" };
      // RE-21: Versand- und Mahndaten räumen; maßgeblich ist wieder die Fälligkeit des Ausstellers
      return done(
        clearDunning({
          ...inv,
          reviewStatus: "revision",
          rejectionReason: payload.reason.trim(),
          rejectionMailSentAt: null,
          sentToBrandAt: null,
          sentToBrandCount: 0,
          lastSentToBrandAt: null,
          documentDueDate: null,
          effectiveDueDate: null,
          collectionAt: null,
          talentPaymentResponse: null,
          talentPaymentResponseAt: null,
          overdueCheckRequestedAt: null,
          adminPaymentConfirmedAt: null,
        }),
      );
    }
    case "send_to_brand": {
      // RE-23/WS-10: Frist beginnt mit dem Versand; Beleg-Fälligkeit wird einmalig festgelegt
      const term = payload.paymentTermDays ?? ctx.settings.defaultPaymentTermDays;
      const docDue = dueDateFrom(t, resolvePaymentTermDays({ document: term, orgDefault: ctx.settings.defaultPaymentTermDays }));
      return done({
        ...inv,
        sentToBrandAt: t,
        sentToBrandCount: 1,
        lastSentToBrandAt: t,
        documentDueDate: docDue,
        effectiveDueDate: docDue,
      });
    }
    case "resend_to_brand":
      // WS-11: erneutes Senden ändert keine Fälligkeit
      return done({ ...inv, sentToBrandCount: inv.sentToBrandCount + 1, lastSentToBrandAt: t });
    case "extend_due_date":
      if (!payload.newDueDate) return { ok: false, reason: "NEW_DUE_DATE_REQUIRED" };
      // WS-10: nur die wirksame Fälligkeit verschiebt sich, der Beleg bleibt
      return done({ ...inv, effectiveDueDate: payload.newDueDate });
    case "request_payment_check":
      // Die neue Nachfrage ersetzt die bisherige Antwort; sonst sähe eine Nachfrage am Antworttag wirkungslos aus (R-09).
      // Die frühere Antwort bleibt im Ereignisprotokoll.
      return done({ ...inv, overdueCheckRequestedAt: t, talentPaymentResponse: null, talentPaymentResponseAt: null });
    case "report_paid": {
      // RE-25: Talent meldet nur; bezahlt erst, wenn die Agentur schon bestätigt hat (RE-27/RE-43-T)
      const next: InvoiceRecord = { ...inv, talentPaymentResponse: "paid", talentPaymentResponseAt: t };
      if (inv.adminPaymentConfirmedAt) return done(clearDunning({ ...next, status: "paid", paidAt: t, collectionAt: null }));
      return done(next);
    }
    case "report_still_overdue":
      return done({ ...inv, talentPaymentResponse: "still_overdue", talentPaymentResponseAt: t });
    case "confirm_payment": {
      // WS-09: bezahlt nur über den Zahlungsweg mit Nachweis
      if (!payload.paymentProofRef?.trim()) return { ok: false, reason: "PAYMENT_PROOF_REQUIRED" };
      return done(
        clearDunning({
          ...inv,
          status: "paid",
          paidAt: t,
          paymentProofRef: payload.paymentProofRef.trim(),
          adminPaymentConfirmedAt: inv.adminPaymentConfirmedAt ?? t,
          collectionAt: null,
        }),
      );
    }
    case "confirm_payout_and_ask":
      // RE-27: Bestätigung + Nachfrage in einem Schritt; bezahlt erst mit Talent-Meldung
      return done({ ...inv, adminPaymentConfirmedAt: t, overdueCheckRequestedAt: t });
    case "revoke_payment_confirmation":
      // RE-28
      return done({ ...inv, adminPaymentConfirmedAt: null, overdueCheckRequestedAt: null, talentPaymentResponse: null, talentPaymentResponseAt: null });
    case "reset_payment":
      // RE-29: exaktes Gegenteil der Bestätigung
      return done({
        ...inv,
        status: "open",
        paidAt: null,
        paymentProofRef: null,
        talentPaymentResponse: null,
        talentPaymentResponseAt: null,
        adminPaymentConfirmedAt: null,
      });
    case "send_dunning": {
      const next = checkDunning(inv, ctx).nextStage!;
      const deadline = dunningDeadlineFor(next, t, {
        override: payload.deadlineOverride ?? null,
        holidays: ctx.holidays ?? [],
        dueDate: currentDueDate(inv),
      });
      // MA-04: Frist gespeichert, Beleg-Fälligkeit bleibt unberührt
      return done({ ...inv, dunningStage: next.key, lastDunningAt: t, dunningDeadline: deadline });
    }
    case "set_dunning_stage": {
      const stage = payload.stage ?? DUNNING_NONE;
      // MA-12: verschickt nichts, gespeicherte Frist fällt weg
      if (stage === DUNNING_NONE) return done(clearDunning(inv));
      return done({ ...inv, dunningStage: stage, dunningDeadline: null });
    }
    case "set_dunning_block":
      return done({ ...inv, dunningBlocked: true });
    case "lift_dunning_block":
      return done({ ...inv, dunningBlocked: false });
    case "hand_over_to_collection":
      return done({ ...inv, dunningStage: DUNNING_COLLECTION, collectionAt: t });
    case "withdraw_collection": {
      // MA-11: zurück auf die Stufe vor der Übergabe (aus dem Protokoll)
      const prev = payload.previousStage && getStage(ctx.policy, payload.previousStage) ? payload.previousStage : finalStage(ctx.policy)?.key ?? DUNNING_NONE;
      return done({ ...inv, dunningStage: prev, collectionAt: null });
    }
    case "cancel":
      return done({ ...inv, status: "cancelled", cancelledAt: t });
    case "edit":
    case "correct":
    case "open_pdf":
    case "review":
    case "check_payment":
    case "check_refund":
      return done(inv);
  }
}

// ---------------------------------------------------------------------------
// Primäraktion (RE-13 … RE-16, RE-31 … RE-35, MA-14 … MA-16)
// ---------------------------------------------------------------------------

export interface PrimaryAction {
  action: InvoiceAction | null;
  /** inert = nur Hinweis, kein Knopf */
  inert: boolean;
  labelKey: string;
  params?: Record<string, string | number>;
}

const act = (action: InvoiceAction, params?: Record<string, string | number>): PrimaryAction => ({ action, inert: false, labelKey: `action.${action}`, ...(params ? { params } : {}) });
const hint = (key: string, params?: Record<string, string | number>): PrimaryAction => ({ action: null, inert: true, labelKey: `hint.${key}`, ...(params ? { params } : {}) });

export function talentPrimaryAction(inv: InvoiceRecord, ctx: InvoiceStateContext): PrimaryAction | null {
  const s = deriveInvoiceState(inv, ctx).state;
  // GELD-R3-1: anteilig ausgezahlt, der Rest folgt; nichts zu melden
  if (partialPayoutOpen(inv) && isSent(inv) && s !== "correction_needed" && s !== "collection") return hint("payout_partial");
  switch (s) {
    case "draft":
      return inv.hasPdf ? act("submit") : { action: "edit", inert: false, labelKey: "action.edit_and_generate_pdf" };
    case "correction_needed":
      return act("correct");
    case "in_review":
      return inv.adminReviewedAt ? null : act("withdraw_submission");
    case "sent":
    case "due_soon":
    case "due_today":
    case "overdue":
    case "dunning":
    case "money_missing":
    case "payout_confirmed":
    case "delivery_pending":
    case "delivery_failed":
      // RE-13/RE-14: im Zahlungslauf nur „Als bezahlt melden“
      return act("report_paid");
    case "payout_overdue":
      return isSent(inv) ? act("report_paid") : null;
    case "paid":
      return inv.hasPdf ? act("open_pdf") : null;
    default:
      return null;
  }
}

export function agencyPrimaryAction(inv: InvoiceRecord, ctx: InvoiceStateContext): PrimaryAction | null {
  const r = deriveInvoiceState(inv, ctx);
  const t = ctx.today;
  // GELD-R3-1: anteilig ausgezahlt; der Rest geht über den Auszahlungslauf, im Modus „nach Zahlungseingang“ erst mit dem Geld der Marke
  if (inv.recipientIsOwnOrg && partialPayoutOpen(inv) && isSent(inv) && r.state !== "collection") {
    return inv.payoutDueDate === null ? hint("waiting_for_brand") : hint("payout_rest");
  }
  // Creator meldet die Überweisung einer Rechnung der Agentur an ihn: Eingang mit Betrag erfassen (CREATOR-R3-6)
  if (inv.kind === "agency_to_talent" && r.state === "payment_reported") return hint("record_transfer");
  switch (r.state) {
    case "discarded":
    case "withdrawn":
    case "cancelled":
    case "credit_note":
    case "paid":
    case "bad_debt":
    case "collection":
    case "correction_needed":
    case "draft":
      return null;
    case "delivery_pending":
      return hint("delivery_pending");
    case "delivery_failed":
      return act("resend_to_brand");
    case "cancelled_refund_pending":
      return act("check_refund");
    case "money_missing":
      return act("check_payment");
    case "payment_reported":
      return act("confirm_payment");
    case "in_review":
      return act("review"); // RE-15: prüfen, nicht blind annehmen
    case "approved":
    case "send_overdue":
      // Auszahlung erst nach Zahlung der Marke: nichts zu tun, bis sie gezahlt hat
      if (inv.recipientIsOwnOrg && inv.payoutDueDate === null) return hint("waiting_for_brand");
      return act("send_to_brand");
    case "payout_overdue":
      return isSent(inv) ? act("confirm_payout_and_ask") : act("send_to_brand");
  }

  // Zahlungslauf
  const due = currentDueDate(inv);
  const overdue = r.isOverdue;

  // MA-16/RE-33: nach eigener Bestätigung keine Mahnung/kein Versand mehr
  if (r.confirmationActive) {
    const lastPush = inv.overdueCheckRequestedAt && inv.adminPaymentConfirmedAt && inv.overdueCheckRequestedAt > inv.adminPaymentConfirmedAt ? inv.overdueCheckRequestedAt : inv.adminPaymentConfirmedAt!;
    const from = addDays(lastPush, ctx.settings.talentAnswerDays);
    return t >= from ? act("request_payment_check", { mode: "preconfirmed" }) : hint("waiting_for_talent", { since: lastPush });
  }

  // MA-14/MA-15: eigene Firma als Empfänger – keine Mahnung, Auszahlung bestätigen & fragen
  if (inv.recipientIsOwnOrg) {
    return overdue || talentSaysStillOverdue(inv) ? act("confirm_payout_and_ask") : null;
  }

  // RE-11/RE-31: offene Nachfrage – Talent am Zug bis Ablauf der Abkühlzeit
  if (r.openPaymentCheck && inv.overdueCheckRequestedAt) {
    const until = addDays(inv.overdueCheckRequestedAt, ctx.settings.paymentCheckCooldownDays);
    return t < until ? hint("talent_turn_until", { date: until }) : act("request_payment_check", { mode: "manual" });
  }

  // MA-08: Mahnsperre
  if (inv.dunningBlocked && overdue) return act("lift_dunning_block");

  // RE-16: letzte Stufe
  if (r.state === "dunning" && isFinalStage(ctx.policy, inv.dunningStage)) {
    const end = r.dunningDeadlineEnd;
    if (end && t <= end) return hint("dunning_deadline_running", { date: end });
    return act("hand_over_to_collection");
  }

  // MA-04/MA-05: eine laufende Mahnfrist gilt auch nach der Antwort „noch offen“ (R-10)
  if (r.state === "dunning") {
    const end = r.dunningDeadlineEnd;
    if (end && t <= end) return hint("dunning_deadline_running", { date: end });
  }

  if (talentSaysStillOverdue(inv)) {
    // RE-35: Antwort vor Fälligkeit – Mahnung erst ab Fälligkeit + 1
    if (due && t <= due) return hint("dunning_from", { date: addDays(due, 1) });
    // RE-32: nach N vollen Tagen wird „Erneut nachfragen“ Hauptknopf
    if (inv.talentPaymentResponseAt && t >= addDays(inv.talentPaymentResponseAt, ctx.settings.stillOverdueRecheckDays)) {
      return act("request_payment_check", { mode: "again" });
    }
    // RE-12: nächster Schritt ist die Mahnung
    return r.canDun ? act("send_dunning") : null;
  }

  if (r.state === "dunning") {
    const end = r.dunningDeadlineEnd;
    if (end && t <= end) return hint("dunning_deadline_running", { date: end });
    return r.canDun ? act("send_dunning") : null;
  }

  // Agenturrechnungen: die Agentur ist Gläubigerin, der nächste Schritt ist die Zahlungserinnerung bzw. Mahnung
  if (overdue) return isAgencyIssued(inv) ? (r.canDun ? act("send_dunning") : null) : act("request_payment_check");
  if (r.state === "due_today") {
    const first = ctx.policy.stages[0];
    return first?.isReminder && r.canDun ? act("send_dunning") : null;
  }
  return null;
}

export function primaryAction(inv: InvoiceRecord, viewer: "agency" | "talent", ctx: InvoiceStateContext): PrimaryAction | null {
  return viewer === "talent" ? talentPrimaryAction(inv, ctx) : agencyPrimaryAction(inv, ctx);
}

// ---------------------------------------------------------------------------
// Rang, Sortierung, Eimer (RE-36, RE-37)
// ---------------------------------------------------------------------------

export const RANK = {
  MONEY_MISSING: 10,
  REFUND: 20,
  PAYMENT_REPORTED: 30,
  SEND_OVERDUE: 40,
  APPROVED: 45,
  IN_REVIEW: 48,
  OVERDUE_ACTION: 50,
  CHECK_BEFORE_DUE: 67,
  DUE_TODAY: 70,
  DUNNING_BLOCKED: 80,
  OTHERS_TURN: 100,
  DONE: 200,
} as const;

export function agencyRank(inv: InvoiceRecord, ctx: InvoiceStateContext): number {
  const r = deriveInvoiceState(inv, ctx);
  if (DONE_STATES.has(r.state)) return RANK.DONE;
  switch (r.state) {
    case "money_missing":
      return RANK.MONEY_MISSING;
    case "cancelled_refund_pending":
      return RANK.REFUND;
    case "payment_reported":
      return RANK.PAYMENT_REPORTED;
    case "send_overdue":
    case "delivery_failed":
      return RANK.SEND_OVERDUE;
    case "payout_overdue":
      return RANK.OVERDUE_ACTION;
    case "delivery_pending":
      return RANK.OTHERS_TURN;
    case "approved":
      return inv.recipientIsOwnOrg && inv.payoutDueDate === null ? RANK.OTHERS_TURN : RANK.APPROVED;
    case "in_review":
      return RANK.IN_REVIEW;
  }
  if (inPaymentRun(inv)) {
    if (inv.dunningBlocked && r.isOverdue) return RANK.DUNNING_BLOCKED;
    const p = agencyPrimaryAction(inv, ctx);
    if (p && !p.inert && (r.isOverdue || r.state === "dunning" || r.confirmationActive)) return RANK.OVERDUE_ACTION;
    if (r.openPaymentCheck && !r.isOverdue) return RANK.CHECK_BEFORE_DUE;
    if (r.state === "due_today") return RANK.DUE_TODAY;
  }
  return RANK.OTHERS_TURN;
}

export type Bucket = "action" | "open" | "done";

export function agencyBucket(inv: InvoiceRecord, ctx: InvoiceStateContext): Bucket {
  const rank = agencyRank(inv, ctx);
  return rank < RANK.OTHERS_TURN ? "action" : rank < RANK.DONE ? "open" : "done";
}

/** RE-37: Eimer aus Talent-Sicht. */
export function talentBucket(inv: InvoiceRecord, ctx: InvoiceStateContext): Bucket {
  const r = deriveInvoiceState(inv, ctx);
  if (DONE_STATES.has(r.state) || r.state === "payment_reported") return "done";
  if (r.state === "draft" || r.state === "correction_needed") return "action";
  if (r.openPaymentCheck && inPaymentRun(inv)) return "action";
  return "open";
}

/** RE-36: Sortierung nach Dringlichkeit, dann Fälligkeit, Betrag, Nummer, ID. */
export function sortByUrgency(invoices: readonly InvoiceRecord[], ctx: InvoiceStateContext): InvoiceRecord[] {
  const keyed = invoices.map((inv) => {
    const dunning = inv.dunningStage !== DUNNING_NONE;
    return { inv, rank: agencyRank(inv, ctx), due: (dunning ? originalDueDate(inv) : currentDueDate(inv)) ?? "9999-12-31" };
  });
  keyed.sort(
    (a, b) =>
      a.rank - b.rank ||
      (a.due < b.due ? -1 : a.due > b.due ? 1 : 0) ||
      b.inv.amountGrossCents - a.inv.amountGrossCents ||
      (a.inv.number ?? "").localeCompare(b.inv.number ?? "") ||
      a.inv.id.localeCompare(b.inv.id),
  );
  return keyed.map((k) => k.inv);
}

// ---------------------------------------------------------------------------
// Listen-Helfer (RE-38, RE-39, RE-41, RE-19)
// ---------------------------------------------------------------------------

/** RE-38: Chip „Alle“ ohne Erledigte; eine Suche findet auch Erledigte. */
export function filterInvoiceList(
  invoices: readonly InvoiceRecord[],
  ctx: InvoiceStateContext,
  opts: { chip: "all" | Bucket; search?: string },
): InvoiceRecord[] {
  const q = opts.search?.trim();
  if (q) {
    const nq = normalizeInvoiceNumber(q);
    return invoices.filter((i) => (i.number ? normalizeInvoiceNumber(i.number).includes(nq) : false));
  }
  return invoices.filter((i) => {
    const b = agencyBucket(i, ctx);
    return opts.chip === "all" ? b !== "done" : b === opts.chip;
  });
}

/**
 * RE-39: offene Forderung = echte Forderungen (nicht Entwurf, Korrektur, storniert,
 * Rückerstattung, bezahlt, verworfen). Gemischte Währungen: keine Gesamtsumme.
 */
export function openReceivables(
  invoices: readonly InvoiceRecord[],
  ctx: InvoiceStateContext,
): { byCurrency: Partial<Record<Currency, Cents>>; total: Cents | null; currency: Currency | null } {
  const excluded: ReadonlySet<InvoiceState> = new Set(["draft", "correction_needed", "cancelled", "cancelled_refund_pending", "credit_note", "paid", "bad_debt", "discarded", "withdrawn"]);
  const by: Partial<Record<Currency, Cents>> = {};
  for (const inv of invoices) {
    if (inv.creditFor || excluded.has(deriveInvoiceState(inv, ctx).state)) continue;
    // Teilzahlungen mindern die offene Forderung (0043)
    by[inv.currency] = (by[inv.currency] ?? 0) + Math.max(0, inv.amountGrossCents - (inv.paidCents ?? 0));
  }
  const cur = Object.keys(by) as Currency[];
  if (cur.length === 1) return { byCurrency: by, total: by[cur[0]!]!, currency: cur[0]! };
  return { byCurrency: by, total: cur.length === 0 ? 0 : null, currency: null };
}

/** RE-41: Das Talent sieht die Ursprungsrechnung, nie die Mahnschreiben an die Marke. */
export function visibleDocuments<T extends { kind: "invoice" | "dunning_letter" | "correction" | "credit_note" }>(
  chain: readonly T[],
  viewer: "agency" | "talent",
): T[] {
  return viewer === "agency" ? [...chain] : chain.filter((d) => d.kind !== "dunning_letter");
}

/** RE-19: Rückfrage vor dem Genehmigen bei zu kurzen Beschreibungen bepreister Positionen. */
export function approvalQuestions(
  lines: readonly { description: string; unitPriceCents: number; quantity: number }[],
  minChars: number,
): Finding[] {
  const short = lines
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.unitPriceCents > 0 && l.quantity > 0 && l.description.trim().length < minChars);
  return short.length ? [{ code: "SHORT_LINE_DESCRIPTIONS", severity: "question", params: { lines: short.map((s) => s.i + 1).join(","), minChars } }] : [];
}
